import { Upload } from "lucide-react";
import { isImageAvatar } from "@kandoo/shared";
import { Avatar } from "./Avatar";
import {
  AVATAR_PRESET_GROUPS,
  AVATAR_PRESET_LABELS,
  presetValue,
} from "./avatarPresets";
import { toast } from "./Toaster";
import { fileToAvatarDataUrl } from "../util/imageAvatar";

/**
 * A grid of avatar choices — "Default" (the identicon), every drawn preset, and
 * an "Upload" tile for a custom image. Selecting one fires `onSelect` with the
 * stored value (`null` for default, `preset:<key>`, or a `data:image/…` URI).
 * Used both for the signed-in user and for owned service users.
 */
export function AvatarPicker({
  userId,
  name,
  value,
  disabled = false,
  onSelect,
}: {
  /** Whose identicon backs the "Default" tile. */
  userId: string;
  /** Accessible name for the preview avatars. */
  name: string;
  /** Currently-selected stored value, or null for the identicon default. */
  value: string | null;
  disabled?: boolean;
  onSelect: (value: string | null) => void;
}) {
  const usingImage = isImageAvatar(value);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // reset so picking the same file again still fires a change
    e.target.value = "";
    if (!file) return;
    try {
      onSelect(await fileToAvatarDataUrl(file));
    } catch (err) {
      toast(err instanceof Error ? err.message : "Could not use that image");
    }
  }

  return (
    <div className="space-y-4">
      {/* Default identicon + custom upload */}
      <div className="flex flex-wrap gap-3">
        <AvatarChoice
          label="Default"
          selected={value === null}
          disabled={disabled}
          onClick={() => onSelect(null)}
        >
          <Avatar userId={userId} name={name} size="md" avatar={null} />
        </AvatarChoice>

        <label
          title="Upload an image"
          aria-label="Upload an image"
          className={`${choiceClass(usingImage)} ${
            disabled ? "cursor-default opacity-50" : "cursor-pointer"
          }`}
        >
          {usingImage ? (
            <span className="inline-block h-8 w-8 overflow-hidden rounded-full ring-2 ring-white">
              <img
                src={value ?? undefined}
                alt="Custom avatar"
                className="h-full w-full object-cover"
              />
            </span>
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <Upload size={16} />
            </span>
          )}
          <span className="text-[11px] text-slate-600">Upload</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={disabled}
            onChange={onFile}
          />
        </label>
      </div>

      {/* Drawn presets, grouped by theme */}
      {AVATAR_PRESET_GROUPS.map((group) => (
        <div key={group.label}>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
            {group.label}
          </div>
          <div className="flex flex-wrap gap-3">
            {group.keys.map((key) => {
              const v = presetValue(key);
              return (
                <AvatarChoice
                  key={key}
                  label={AVATAR_PRESET_LABELS[key]}
                  selected={value === v}
                  disabled={disabled}
                  onClick={() => onSelect(v)}
                >
                  <Avatar
                    userId={userId}
                    name={AVATAR_PRESET_LABELS[key]}
                    size="md"
                    avatar={v}
                  />
                </AvatarChoice>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const choiceClass = (selected: boolean) =>
  `flex flex-col items-center gap-1 rounded-lg border p-2 transition ${
    selected
      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
      : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
  }`;

function AvatarChoice({
  label,
  selected,
  disabled,
  onClick,
  children,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={selected}
      className={`${choiceClass(selected)} disabled:opacity-50`}
    >
      {children}
      <span className="text-[11px] text-slate-600">{label}</span>
    </button>
  );
}
